import { useToast } from '@chakra-ui/react';
import { Cart } from '@diamond/shared/types';
import { showToast } from '@diamond/shared/ui';
import { calculateFinalPrice, trackEvent } from '@diamond/shared/utils';
import { TRACKING_EVENTS, TRACKING_PAGES } from '@diamond/sol-buyer/constant';
import { useAuthStore, useVoucherStore } from '@diamond/sol-buyer-context';
import { useRouter } from 'next/router';
import { useCallback } from 'react';
import shallow from 'zustand/shallow';

import { extractVoucherCodes } from '../utils/extractVoucherCodes';
import { useCartActions } from './useCartActions';

export const useCartCheckout = () => {
  const router = useRouter();
  const toast = useToast();
  const token = useAuthStore((state) => state.access_token);

  const notifyWrapper = (type: 'success' | 'error', message: string) => {
    showToast(toast, type, message);
  };

  const { createOrder } = useCartActions({ token, notify: notifyWrapper });
  const appliedVouchers = useVoucherStore(
    (state) => state.appliedVouchers,
    shallow
  );

  const handleCheckout = useCallback(
    async (items: Cart[], warehouseId: string) => {
      try {
        const products = items.map((item) => ({
          id: item.product.id,
          quantity: item.quantity,
          note: item.note || null,
        }));

        // Extract voucher codes from applied vouchers
        const voucher_codes = extractVoucherCodes(appliedVouchers);

        const response = await createOrder({
          warehouse_id: warehouseId,
          products,
          voucher_codes: voucher_codes.length > 0 ? voucher_codes : undefined,
        });

        const totalValue = items.reduce((sum, item) => {
          const finalPrice = calculateFinalPrice(
            item.product,
            item.quantity / 1000
          );
          return sum + finalPrice * (item.quantity / 1000);
        }, 0);

        const ecommerceItems = items.map((item, index) => {
          const finalPrice = calculateFinalPrice(
            item.product,
            item.quantity / 1000
          );
          const regularPrice = item.product.prices?.[0]?.price || 0;
          const discountAmount =
            regularPrice > finalPrice ? regularPrice - finalPrice : 0;

          const discountPromo = item.product.discount_promos?.[0];
          const bundledDiscountPromo =
            item.product.bundled_discount_promos?.[0];
          const freeGoods = item.product.free_good_promos?.[0];
          const bundledFreeGoods = item.product.bundled_free_good_promos?.[0];
          const promoForTracking =
            bundledDiscountPromo ||
            discountPromo ||
            bundledFreeGoods ||
            freeGoods ||
            null;

          return {
            id: item.id,
            item_id: item.product.item_code?.toString(),
            item_name: item.product.title,
            item_brand: item.product.brand_name,
            affiliation: item.product.brand_desc,
            coupon: promoForTracking?.promo_code || '',
            discount: discountAmount > 0 ? discountAmount : 0,
            index: index,
            item_category: item.product.category_name,
            item_category2: item.product.sub_category_name,
            item_list_id: 'cart',
            item_list_name: 'Cart',
            item_variant: item.product.type || '',
            location_id: warehouseId,
            price: finalPrice,
            quantity: item.quantity / 1000 || 1,
          };
        });

        const voucherParams =
          appliedVouchers.length > 0
            ? {
                voucher_id: appliedVouchers.map((v) => v.code).join(','),
                voucher_name: appliedVouchers.map((v) => v.title).join(','),
              }
            : {};

        trackEvent(TRACKING_EVENTS.BEGIN_CHECKOUT, {
          currency: 'IDR',
          value: totalValue,
          transaction_id: response.id,
          items: ecommerceItems,
          section_name: TRACKING_PAGES.CART,
          page_name: TRACKING_PAGES.CART,
          ...voucherParams,
        });

        await router.push({
          pathname: '/checkout-new',
          query: {
            meta_id: response.id,
            warehouse: warehouseId,
          },
        });
      } catch (error: any) {
        const idToast = 'minimum-order';
        if (!toast.isActive(idToast)) {
          showToast(
            toast,
            'error',
            error.message,
            idToast,
            undefined,
            undefined,
            { maxW: '38rem' }
          );
        }
        throw error;
      }
    },
    [appliedVouchers, createOrder, router, toast]
  );

  return {
    handleCheckout,
  };
};
